import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PreviewIcon from '@mui/icons-material/Preview';
import SyncIcon from '@mui/icons-material/Sync';
import {
  extractApiErrorMessage,
  googleSheetsService,
  GoogleSheetsConfig,
  GoogleSheetsPreviewResponse,
  GoogleSheetsSyncResponse,
  MOVEMENT_TYPE_OPTIONS,
  OPTIONAL_MAPPING_FIELDS,
  REQUIRED_MAPPING_FIELDS,
} from '../../../../core/modules/googleSheets/services/api';

const NONE_VALUE = '__none__';

const GoogleSheetsPage = () => {
  const { id: companyIdParam } = useParams<{ id: string }>();
  const companyId = Number(companyIdParam);

  const [config, setConfig] = useState<GoogleSheetsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Paso 1: ubicación del sheet
  const [spreadsheetUrl, setSpreadsheetUrl] = useState('');
  const [sheetTabName, setSheetTabName] = useState('');
  const [headerRow, setHeaderRow] = useState(1);

  // Paso 2: vista previa
  const [preview, setPreview] = useState<GoogleSheetsPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Paso 3: mapeo de columnas
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});

  // Paso 4: mapeo de valores de tipo de movimiento
  const [distinctTypeValues, setDistinctTypeValues] = useState<string[] | null>(null);
  const [distinctValuesLoading, setDistinctValuesLoading] = useState(false);
  const [movementTypeMapping, setMovementTypeMapping] = useState<Record<string, string>>({});

  const [savingMapping, setSavingMapping] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<GoogleSheetsSyncResponse | null>(null);

  const loadConfig = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await googleSheetsService.getConfig(companyId);
      setConfig(data);
      if (data.spreadsheetUrl) setSpreadsheetUrl(data.spreadsheetUrl);
      if (data.sheetTabName) setSheetTabName(data.sheetTabName);
      if (data.headerRow) setHeaderRow(data.headerRow);
      if (data.columnMapping) setColumnMapping(data.columnMapping);
      if (data.movementTypeValueMapping) setMovementTypeMapping(data.movementTypeValueMapping);
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'No se pudo cargar la configuración de Google Sheets.'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleCopyEmail = () => {
    if (config?.serviceAccountEmail) {
      navigator.clipboard.writeText(config.serviceAccountEmail);
      setInfo('Email copiado.');
    }
  };

  const handlePreview = async () => {
    if (!companyId || !spreadsheetUrl.trim()) return;
    setPreviewLoading(true);
    setError(null);
    setInfo(null);
    setDistinctTypeValues(null);
    try {
      const result = await googleSheetsService.preview(companyId, {
        spreadsheetUrl,
        sheetTabName: sheetTabName || undefined,
        headerRow,
      });
      setPreview(result);
    } catch (err: any) {
      setPreview(null);
      setError(extractApiErrorMessage(err, 'No se pudo leer el sheet. Revisa el link y que esté compartido.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleMappingChange = (field: string, value: string) => {
    setColumnMapping((prev) => {
      const next = { ...prev };
      if (value === NONE_VALUE) {
        delete next[field];
      } else {
        next[field] = value;
      }
      return next;
    });
    if (field === 'movement_type') {
      setDistinctTypeValues(null);
      setMovementTypeMapping({});
    }
  };

  const handleLoadDistinctValues = async () => {
    const typeColumn = columnMapping.movement_type;
    if (!companyId || !typeColumn) return;
    setDistinctValuesLoading(true);
    setError(null);
    try {
      const values = await googleSheetsService.getDistinctValues(companyId, {
        spreadsheetUrl,
        sheetTabName: sheetTabName || undefined,
        headerRow,
        columnName: typeColumn,
      });
      setDistinctTypeValues(values);
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'No se pudieron traer los valores de esa columna.'));
    } finally {
      setDistinctValuesLoading(false);
    }
  };

  const handleTypeValueMappingChange = (rawValue: string, mappedValue: string) => {
    setMovementTypeMapping((prev) => {
      const next = { ...prev };
      if (mappedValue === NONE_VALUE) {
        delete next[rawValue];
      } else {
        next[rawValue] = mappedValue;
      }
      return next;
    });
  };

  const requiredFieldsMapped = REQUIRED_MAPPING_FIELDS.every((f) => !!columnMapping[f.key]);
  const unmappedTypeValuesCount = (distinctTypeValues || []).filter((v) => !movementTypeMapping[v]).length;

  const handleSaveMapping = async () => {
    if (!companyId) return;
    setSavingMapping(true);
    setError(null);
    setInfo(null);
    try {
      const result = await googleSheetsService.saveMapping(companyId, {
        spreadsheetUrl,
        sheetTabName: sheetTabName || undefined,
        headerRow,
        columnMapping,
        movementTypeValueMapping: movementTypeMapping,
      });
      setConfig(result);
      setInfo('Mapeo guardado. Ya puedes sincronizar cuando quieras.');
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'No se pudo guardar el mapeo.'));
    } finally {
      setSavingMapping(false);
    }
  };

  const handleSync = async () => {
    if (!companyId) return;
    setSyncing(true);
    setError(null);
    setInfo(null);
    setSyncResult(null);
    try {
      const result = await googleSheetsService.sync(companyId);
      setSyncResult(result);
      const refreshed = await googleSheetsService.getConfig(companyId);
      setConfig(refreshed);
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'No se pudo sincronizar.'));
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Box className="container" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="container" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Google Sheets
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Conecta el Google Sheet donde registras tus movimientos de stock -- Inventaria lo va a leer
        periódicamente y va a calcular con esos datos las mismas alertas y métricas que calcula
        para una empresa con ERP/WMS conectado. El mapeo de columnas se hace una sola vez aquí abajo;
        después solo hace falta mantener el sheet actualizado.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            1. Compartir el sheet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            En tu Google Sheet, haz clic en "Compartir" y agrega este email como Lector:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={config?.serviceAccountEmail || 'No configurado en el servidor'}
              sx={{ fontFamily: 'monospace' }}
            />
            {config?.serviceAccountEmail && (
              <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopyEmail}>
                Copiar
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            2. Conectar el spreadsheet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pega el link completo del Google Sheet (el de la barra de direcciones), indica en qué
            pestaña y en qué fila están los encabezados, y trae una vista previa para confirmar que
            Inventaria puede leerlo.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                label="Link del Google Sheet"
                fullWidth
                value={spreadsheetUrl}
                onChange={(e) => setSpreadsheetUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Pestaña (opcional)"
                fullWidth
                value={sheetTabName}
                onChange={(e) => setSheetTabName(e.target.value)}
                placeholder="Primera por defecto"
                select={!!preview?.availableTabs?.length}
              >
                {preview?.availableTabs?.map((tab) => (
                  <MenuItem key={tab} value={tab}>
                    {tab}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Fila de encabezados"
                type="number"
                fullWidth
                value={headerRow}
                onChange={(e) => setHeaderRow(Math.max(1, Number(e.target.value) || 1))}
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>
          <Button
            variant="contained"
            startIcon={previewLoading ? <CircularProgress size={16} color="inherit" /> : <PreviewIcon />}
            onClick={handlePreview}
            disabled={previewLoading || !spreadsheetUrl.trim()}
            sx={{ mt: 2 }}
          >
            Traer vista previa
          </Button>

          {preview && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Pestaña <strong>{preview.worksheetTitle}</strong> -- {preview.totalDataRows} filas de datos
                encontradas.
              </Typography>
              <TableContainer sx={{ maxHeight: 240 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {preview.headers.map((h) => (
                        <TableCell key={h}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.sampleRows.map((row, idx) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <TableRow key={idx}>
                        {preview.headers.map((h) => (
                          <TableCell key={h}>{row[h] ?? ''}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>

      {preview && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              3. Mapear columnas
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Dile a Inventaria qué columna de tu sheet corresponde a cada dato. Esto se hace una
              sola vez -- si más adelante cambias el orden o los nombres de las columnas en tu
              sheet, vuelve a esta pantalla y rehaz el mapeo.
            </Typography>
            <Grid container spacing={2}>
              {REQUIRED_MAPPING_FIELDS.map((field) => (
                <Grid item xs={12} sm={6} key={field.key}>
                  <TextField
                    select
                    label={`${field.label} *`}
                    fullWidth
                    value={columnMapping[field.key] || NONE_VALUE}
                    onChange={(e) => handleMappingChange(field.key, e.target.value)}
                  >
                    <MenuItem value={NONE_VALUE}>-- Selecciona una columna --</MenuItem>
                    {preview.headers.map((h) => (
                      <MenuItem key={h} value={h}>
                        {h}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ))}
              {OPTIONAL_MAPPING_FIELDS.map((field) => (
                <Grid item xs={12} sm={6} key={field.key}>
                  <TextField
                    select
                    label={`${field.label} (opcional)`}
                    fullWidth
                    value={columnMapping[field.key] || NONE_VALUE}
                    onChange={(e) => handleMappingChange(field.key, e.target.value)}
                  >
                    <MenuItem value={NONE_VALUE}>-- No aplica --</MenuItem>
                    {preview.headers.map((h) => (
                      <MenuItem key={h} value={h}>
                        {h}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {preview && columnMapping.movement_type && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              4. Mapear los tipos de movimiento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tu columna "{columnMapping.movement_type}" puede tener textos distintos a los que usa
              Inventaria (ej. "Venta boleta" en vez de "Venta"). Trae los valores que encontramos en
              tu sheet y dinos a qué tipo corresponde cada uno -- una fila con un valor sin mapear
              se va a omitir en la sincronización.
            </Typography>
            <Button
              variant="outlined"
              startIcon={distinctValuesLoading ? <CircularProgress size={16} /> : undefined}
              onClick={handleLoadDistinctValues}
              disabled={distinctValuesLoading}
              sx={{ mb: 2 }}
            >
              {distinctTypeValues ? 'Actualizar valores encontrados' : 'Traer valores encontrados'}
            </Button>

            {distinctTypeValues && distinctTypeValues.length === 0 && (
              <Alert severity="warning">No se encontraron valores en esa columna todavía.</Alert>
            )}

            {distinctTypeValues && distinctTypeValues.length > 0 && (
              <>
                {unmappedTypeValuesCount > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {unmappedTypeValuesCount} valor(es) todavía sin mapear.
                  </Alert>
                )}
                <Grid container spacing={2}>
                  {distinctTypeValues.map((rawValue) => (
                    <Grid item xs={12} sm={6} key={rawValue}>
                      <TextField
                        select
                        label={rawValue}
                        fullWidth
                        value={movementTypeMapping[rawValue] || NONE_VALUE}
                        onChange={(e) => handleTypeValueMappingChange(rawValue, e.target.value)}
                      >
                        <MenuItem value={NONE_VALUE}>-- Sin mapear (se omite) --</MenuItem>
                        {MOVEMENT_TYPE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {preview && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Button
              variant="contained"
              onClick={handleSaveMapping}
              disabled={savingMapping || !requiredFieldsMapped}
            >
              {savingMapping ? <CircularProgress size={20} /> : 'Guardar mapeo'}
            </Button>
            {!requiredFieldsMapped && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                Completa todas las columnas obligatorias (*) para poder guardar.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {config?.mappingConfirmedAt && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              5. Sincronización
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              El mapeo ya está guardado. Cada sincronización lee el sheet completo, agrega los
              movimientos nuevos, y recalcula las alertas y métricas afectadas -- las filas ya
              sincronizadas no se vuelven a procesar (si editaste una fila vieja en el sheet, ese
              cambio no se va a reflejar solo).
            </Typography>

            {config.lastSyncedAt && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip
                  label={`Última sincronización: ${new Date(config.lastSyncedAt).toLocaleString('es-CL')}`}
                />
                <Chip
                  label={config.lastSyncStatus === 'success' ? 'Exitosa' : 'Con error'}
                  color={config.lastSyncStatus === 'success' ? 'success' : 'error'}
                />
                {config.lastSyncRowCount !== null && (
                  <Chip label={`${config.lastSyncRowCount} filas nuevas`} />
                )}
                {config.lastSyncSkippedRowCount !== null && (
                  <Chip label={`${config.lastSyncSkippedRowCount} ya existentes`} />
                )}
              </Box>
            )}
            {config.lastSyncError && <Alert severity="error" sx={{ mb: 2 }}>{config.lastSyncError}</Alert>}

            <Divider sx={{ my: 2 }} />

            <Button
              variant="contained"
              startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              onClick={handleSync}
              disabled={syncing}
            >
              Sincronizar ahora
            </Button>

            {syncResult && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Chip label={`${syncResult.rowCount} filas leídas`} />
                  <Chip label={`${syncResult.createdCount} nuevas`} color="success" />
                  <Chip label={`${syncResult.skippedCount} ya existentes`} />
                  {syncResult.errorCount > 0 && (
                    <Chip label={`${syncResult.errorCount} con error`} color="warning" />
                  )}
                </Box>
                {syncResult.errors.length > 0 && (
                  <Alert severity="warning">
                    {syncResult.errors.slice(0, 10).map((e) => (
                      <div key={e}>{e}</div>
                    ))}
                  </Alert>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default GoogleSheetsPage;
